import { useCallback, useEffect, useRef, useState } from 'react';

import { TWO } from '@/constants';
import { SessionWarningAction } from '@/types';
import { isInNumberOfDays } from '@/utils';

import CoachCancelSessionDialog from './CoachCancelSessionDialog';
import CoachRescheduleSession from './CoachRescheduleSession';
import { isAdminLegacyMomentsSession } from './sessionData';

import type { CalendarActionSubject } from '@/features/appointments/calendarAppointmentProjection';
import type { Session } from '@/types';
import type { ReactNode } from 'react';

import CancelSession from '@/components/Cal/cancelSession';
import RescheduleSession from '@/components/Cal/rescheduleSession';
import SessionWarning from '@/components/Session/SessionWarning';
import { api } from '@/store/api';
import { useUpdateSessionMutation } from '@/store/api/sessionApi';
import { useAppDispatch } from '@/store/hooks';
import { sessionActionRoute } from '@/utils/sessionActionRoute';

type ActiveAction = {
  action:
    'reschedule' | 'cancel' | 'coach-native-cancel' | 'coach-native-reschedule';
  session: Session;
  subject: CalendarActionSubject;
  returnFocus?: () => void;
} | null;

type PendingWarning = {
  action: SessionWarningAction;
  cancelAction: 'cancel' | 'coach-native-cancel';
  rescheduleAction: 'reschedule' | 'coach-native-reschedule';
  session: Session;
  subject: CalendarActionSubject;
  returnFocus?: () => void;
} | null;

type FocusTarget = HTMLElement | (() => void) | null;

export interface SessionActions {
  openReschedule: (
    subject: CalendarActionSubject | Session,
    focusTarget?: FocusTarget,
  ) => void;
  openCancel: (
    subject: CalendarActionSubject | Session,
    focusTarget?: FocusTarget,
  ) => Promise<void>;
  modals: ReactNode;
}

export interface SessionActionEvent {
  action: 'cancel' | 'reschedule';
  isWarning: boolean;
  session: Session;
}

interface SessionActionOptions {
  adminCoachId?: string;
  disabled?: boolean;
  nativeMomentsActionScope?: 'all' | 'none' | 'recurring';
  onAction?: (event: SessionActionEvent) => void;
  onClose?: () => void;
}

const hasNativeAppointmentIdentity = (session: Session): boolean =>
  Boolean(session.sourceAppointmentId);

const normalizeActionSubject = (
  subject: CalendarActionSubject | Session,
): CalendarActionSubject =>
  'source' in subject ? subject : { session: subject, source: 'session' };

// MEN-4122: the participant/admin calendar records deliberately blank
// `session.user.id` and `session.coach.id` so a Moments person id can never be
// mistaken for a Rails `User.id`. Every dialog reached from here is
// Rails-scoped, so re-attach the ids from the paired Rails session when one
// exists. Without this, CoachRescheduleSession falls back to matching the
// member's display name against `coachees` — which resolves to nothing when
// two members share a name, and skips the scheduling-context request with no
// error shown. An unpaired native has no Rails id to attach and is left as-is.
const actionSubjectSession = (subject: CalendarActionSubject): Session => {
  if (subject.source === 'session') return subject.session;
  const { railsSession } = subject;
  if (!railsSession) return subject.session;
  // Fill only what the record blanked. The platform-admin projection already
  // carries Rails ids, so overwriting them from a mispaired Rails session
  // would replace good data rather than close a gap.
  return {
    ...subject.session,
    coach: {
      ...subject.session.coach,
      id: subject.session.coach.id || railsSession.coach.id,
    },
    user: {
      ...subject.session.user,
      id: subject.session.user.id || railsSession.user.id,
    },
  };
};

const isNativeMomentsCancelSession = (
  session: Session,
  scope: NonNullable<SessionActionOptions['nativeMomentsActionScope']>,
): boolean => scope !== 'none' && hasNativeAppointmentIdentity(session);

export const sessionNeedsActionWarning = (session: Session): boolean =>
  isInNumberOfDays(session.startedAt, TWO);

const rescheduleActionFor = (
  session: Session,
  scope: NonNullable<SessionActionOptions['nativeMomentsActionScope']>,
): NonNullable<PendingWarning>['rescheduleAction'] =>
  scope !== 'none' &&
  sessionActionRoute({
    action: 'reschedule',
    session,
    viewer: 'associated-coach',
  }) === 'mento-coach'
    ? 'coach-native-reschedule'
    : 'reschedule';

const cancelActionFor = (
  session: Session,
  scope: NonNullable<SessionActionOptions['nativeMomentsActionScope']>,
): NonNullable<PendingWarning>['cancelAction'] =>
  isNativeMomentsCancelSession(session, scope)
    ? 'coach-native-cancel'
    : 'cancel';

const actionsFor = (
  subject: CalendarActionSubject,
  adminCoachId: string | undefined,
  scope: NonNullable<SessionActionOptions['nativeMomentsActionScope']>,
): Pick<NonNullable<PendingWarning>, 'cancelAction' | 'rescheduleAction'> => {
  const { session } = subject;
  if (adminCoachId) {
    // MEN-5338: PR #1466 narrowed this to native Appointments, which sent
    // Rails-owned Moments series to "contact support". The Ops drawers' admin
    // Session branch handles them; Cal and unknown provenance keep the Cal flow.
    const isMoments =
      subject.source === 'platform-admin-appointment' ||
      (subject.source === 'session' &&
        isAdminLegacyMomentsSession(subject.session));
    return {
      cancelAction: isMoments ? 'coach-native-cancel' : 'cancel',
      rescheduleAction: isMoments ? 'coach-native-reschedule' : 'reschedule',
    };
  }

  return {
    cancelAction: cancelActionFor(session, scope),
    rescheduleAction: rescheduleActionFor(session, scope),
  };
};

// Centralizes coach reschedule/cancel for the Schedule views (MEN-4160). Reuses
// the provider-aware Cal/Moments flows; this records intent while final
// cancellation state/attribution stays owned by backend/provider success paths.
export const useSessionActions = (
  timeZone?: string | null,
  {
    adminCoachId,
    disabled = false,
    nativeMomentsActionScope = 'all',
    onAction,
    onClose,
  }: SessionActionOptions = {},
): SessionActions => {
  const [active, setActive] = useState<ActiveAction>(null);
  const [warning, setWarning] = useState<PendingWarning>(null);
  const disabledRef = useRef(disabled);
  const returnFocusRef = useRef<(() => void) | undefined>();
  disabledRef.current = disabled;
  const dispatch = useAppDispatch();
  const [updateSession] = useUpdateSessionMutation();

  const focusRestorationFor = useCallback(
    (focusTarget?: FocusTarget): (() => void) | undefined => {
      if (typeof focusTarget === 'function') return focusTarget;
      const element =
        focusTarget === undefined &&
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : focusTarget;
      return element ? () => element.focus() : undefined;
    },
    [],
  );

  useEffect(() => {
    if (!disabled) return;
    setActive(null);
    setWarning(null);
  }, [disabled]);

  const close = useCallback(() => {
    dispatch(api.util.invalidateTags(['Session']));
    onClose?.();
    setActive(null);
    const returnFocus = returnFocusRef.current;
    returnFocusRef.current = undefined;
    window.requestAnimationFrame(() => returnFocus?.());
  }, [dispatch, onClose]);

  const openReschedule = useCallback(
    (input: CalendarActionSubject | Session, focusTarget?: FocusTarget) => {
      if (disabledRef.current) return;
      const subject = normalizeActionSubject(input);
      const session = actionSubjectSession(subject);
      const actions = actionsFor(
        subject,
        adminCoachId,
        nativeMomentsActionScope,
      );
      const returnFocus = focusRestorationFor(focusTarget);
      if (
        actions.rescheduleAction !== 'coach-native-reschedule' &&
        sessionNeedsActionWarning(session)
      ) {
        onAction?.({ action: 'reschedule', isWarning: true, session });
        setWarning({
          action: SessionWarningAction.RESCHEDULE,
          ...actions,
          returnFocus,
          session,
          subject,
        });
        return;
      }
      returnFocusRef.current = returnFocus;
      onAction?.({ action: 'reschedule', isWarning: false, session });
      setActive({
        action: actions.rescheduleAction,
        returnFocus,
        session,
        subject,
      });
    },
    [adminCoachId, focusRestorationFor, nativeMomentsActionScope, onAction],
  );

  const recordCancellationIntent = useCallback(
    (session: Session) =>
      updateSession({
        data: { cancellationRequestedBy: 'coach' },
        id: session.id,
      }).unwrap(),
    [updateSession],
  );

  const openCancel = useCallback(
    async (
      input: CalendarActionSubject | Session,
      focusTarget?: FocusTarget,
    ) => {
      if (disabledRef.current) return;
      const subject = normalizeActionSubject(input);
      const session = actionSubjectSession(subject);
      const actions = actionsFor(
        subject,
        adminCoachId,
        nativeMomentsActionScope,
      );
      const returnFocus = focusRestorationFor(focusTarget);
      if (
        actions.cancelAction !== 'coach-native-cancel' &&
        sessionNeedsActionWarning(session)
      ) {
        onAction?.({ action: 'cancel', isWarning: true, session });
        setWarning({
          action: SessionWarningAction.CANCEL,
          ...actions,
          returnFocus,
          session,
          subject,
        });
        return;
      }
      onAction?.({ action: 'cancel', isWarning: false, session });
      if (actions.cancelAction === 'coach-native-cancel') {
        returnFocusRef.current = returnFocus;
        setActive({
          action: 'coach-native-cancel',
          returnFocus,
          session,
          subject,
        });
        return;
      }
      await recordCancellationIntent(session);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This ref can change while the request is in flight.
      if (disabledRef.current) return;
      returnFocusRef.current = returnFocus;
      setActive({ action: 'cancel', returnFocus, session, subject });
    },
    [
      adminCoachId,
      focusRestorationFor,
      nativeMomentsActionScope,
      onAction,
      recordCancellationIntent,
    ],
  );

  let modals: ReactNode = null;
  if (active?.action === 'coach-native-reschedule') {
    modals = (
      <CoachRescheduleSession
        key={active.session.sourceAppointmentId ?? active.session.id}
        actionSubject={active.subject}
        adminCoachId={adminCoachId}
        close={close}
        session={active.session}
        timeZone={timeZone}
      />
    );
  } else if (active?.action === 'reschedule') {
    modals = <RescheduleSession close={close} session={active.session} />;
  } else if (active?.action === 'coach-native-cancel') {
    modals = (
      <CoachCancelSessionDialog
        key={active.session.sourceAppointmentId ?? active.session.id}
        actionSubject={active.subject}
        adminCoachId={adminCoachId}
        close={close}
        session={active.session}
        timeZone={timeZone}
      />
    );
  } else if (active?.action === 'cancel') {
    modals = <CancelSession close={close} session={active.session} />;
  } else if (warning) {
    modals = (
      <SessionWarning
        action={warning.action}
        adminMode={Boolean(adminCoachId)}
        close={() => setWarning(null)}
        session={warning.session}
        onCancel={(session) => {
          if (warning.cancelAction === 'coach-native-cancel') {
            setWarning(null);
            setActive({
              action: 'coach-native-cancel',
              returnFocus: warning.returnFocus,
              session,
              subject: warning.subject,
            });
            returnFocusRef.current = warning.returnFocus;
            return;
          }
          recordCancellationIntent(session)
            .then(() => {
              if (disabledRef.current) return;
              setWarning(null);
              setActive({
                action: 'cancel',
                returnFocus: warning.returnFocus,
                session,
                subject: warning.subject,
              });
              returnFocusRef.current = warning.returnFocus;
            })
            .catch(() => undefined);
        }}
        onReschedule={(session) => {
          setWarning(null);
          setActive({
            action: warning.rescheduleAction,
            returnFocus: warning.returnFocus,
            session,
            subject: warning.subject,
          });
          returnFocusRef.current = warning.returnFocus;
        }}
      />
    );
  }

  return { modals, openCancel, openReschedule };
};
